<script setup>
import { defineEmits, defineProps, ref, watch, computed } from "vue";
import PaymentMethod from "@/components/payment/PaymentMethod.vue";
import { formatMoney } from "@/utils/money.ts";
import dayjs from "dayjs";
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RefreshCcw, BanknoteArrowDown, BanknoteArrowUp, Trash2 } from "@lucide/vue";
import { Button } from "@/shadcn/components/ui/button";
import InputPrice from "@/components/ui/InputPrice.vue";
import {
  AlertDialog,
  AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger
} from "@/shadcn/components/ui/alert-dialog/index.js";
import { Label } from "reka-ui";

const props = defineProps({
  payment: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'close',
  'payment:updated'
]);

const refreshingPayment = ref(false);
const paymentDetail = ref(props.payment);
const amountToCapture = ref(props.payment?.capturable_amount ? props.payment.capturable_amount.amount : null);
const isDeposit = computed(() => paymentDetail.value?.payable_type === 'App\\Models\\Deposit');

watch(
  () => props.payment,
  (newPayment) => {
    if (newPayment) {
      paymentDetail.value = newPayment;
      amountToCapture.value = newPayment?.capturable_amount ? newPayment.capturable_amount.amount : null;
    } else {
      paymentDetail.value = null;
      amountToCapture.value = null;
    }
  },
  { immediate: true }
)

const onClickRefreshPayment = async () => {
  refreshingPayment.value = true;
  paymentDetail.value = await paymentDetail.value.refresh();
  emit('payment:updated', paymentDetail.value);
  refreshingPayment.value = false;
};

const onClickCancelPayment = async () => {
  await paymentDetail.value.cancel();
  emit('payment:updated', paymentDetail.value);
  emit('close');
};

const onClickCapturePayment = async () => {
  await paymentDetail.value.capture(amountToCapture.value);
  emit('payment:updated', paymentDetail.value);
  emit('close');
};

const onDialogUpdate = (isOpen) => {
  if (!isOpen) {
    emit('close');
  }
};
</script>

<template>
  <Dialog :open="!! paymentDetail" @update:open="onDialogUpdate">
    <DialogScrollContent size="lg">
      <DialogHeader>
        <DialogTitle>
          {{ $t('Payment details') }}
        </DialogTitle>
      </DialogHeader>
      <div v-if="paymentDetail">
        <Button variant="outline"
                size="sm"
                :loading="refreshingPayment"
                @click.prevent="onClickRefreshPayment(paymentDetail)">
          <RefreshCcw class="tw:w-5 tw:h-5" />
          {{ $t('Refresh')}}
        </Button>
        <dl class="row mt-3" v-if="!! paymentDetail">
          <template v-for="detail in [
            { key: $t('Amount:'), value: paymentDetail.due_amount ? formatMoney(paymentDetail.due_amount) : 'N/A' },
            { key: $t('Payment type:'), value: paymentDetail.type.label },
            { key: $t('Payment status:'), value: paymentDetail.status },
            paymentDetail.payment_provider ? { key: $t('Payment provider:'), value: paymentDetail.payment_provider.name } : null,
            { key: $t('Payment mode:'), value: paymentDetail.mode.label },
            { key: $t('Payment reference:'), value: paymentDetail.reference ?? 'N/A' },
            { key: $t('Creation date:'), value: dayjs(paymentDetail.created_at).format('LLL') },
            { key: $t('Update date:'), value: paymentDetail.updated_at ? dayjs(paymentDetail.updated_at).format('LLL') : 'N/A'},
            { key: $t('Note:'), value: paymentDetail.note ?? '-' },
            { key: $t('Paid amount:'), value : paymentDetail.paid_amount ? formatMoney(paymentDetail.paid_amount) : 'N/A'},
            { key: $t('Payment fee:'), value : paymentDetail.payment_fee ? formatMoney(paymentDetail.payment_fee) : 'N/A'},
            { key: $t('Platform fee:'), value : paymentDetail.platform_fee ? formatMoney(paymentDetail.platform_fee) : 'N/A'},
            { key: $t('Net amount:'), value : paymentDetail.net_amount ? formatMoney(paymentDetail.net_amount) : 'N/A'},
            { key: $t('Capturable amount:'), value : paymentDetail.capturable_amount ? formatMoney(paymentDetail.capturable_amount) : 'N/A'},
          ].filter(Boolean)">
            <dt class="col-sm-5">
              {{ detail.key }}
            </dt>
            <dd class="col-sm-7">
              {{ detail.value }}
            </dd>
          </template>
          <template v-if="paymentDetail.mode.value === 'stripe'">
            <dt class="col-sm-5">
              {{ $t('Payment method:') }}
            </dt>
            <dd class="col-sm-7">
              <PaymentMethod :payment-method="paymentDetail.payment_method" />
            </dd>
            <dt class="col-sm-5">
              {{ $t('Stripe dashboard:') }}
            </dt>
            <dd class="col-sm-7">
              <a :href="`https://dashboard.stripe.com/payments/${paymentDetail.reference}`" target="_blank">
                {{ $t('View on Stripe') }}
              </a>
            </dd>
          </template>
        </dl>
      </div>
      <DialogFooter>
        <template v-if="isDeposit">
          <AlertDialog v-if="paymentDetail?.canBeCaptured()">
            <AlertDialogTrigger as-child>
              <Button variant="ghost">
                <BanknoteArrowDown class="tw:w-5 tw:h-5" />
                {{ $t('Capture deposit') }}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{{ $t('Capture deposit') }}</AlertDialogTitle>
                <AlertDialogDescription>
                  <div class="form-group">
                    <label for="frm-capture-amount">{{ $t('Amount to capture') }}</label>
                    <InputPrice
                      id="frm-capture-amount"
                      v-model="amountToCapture"
                      :currency="paymentDetail.capturable_amount?.currency"
                    />
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{{ $t('Keep deposit') }}</AlertDialogCancel>
                <AlertDialogAction @click.prevent="onClickCapturePayment">
                  {{ $t('Confirm deposit capture') }}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </template>

        <AlertDialog v-if="paymentDetail?.canBeCanceled()">
          <AlertDialogTrigger as-child>
            <Button type="button" variant="ghost-destructive">
              <BanknoteArrowUp class="tw:w-5 tw:h-5" />
              {{ isDeposit ? $t('Cancel deposit') : $t('Cancel payment') }}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{{ isDeposit ? $t('Cancel deposit') : $t('Cancel payment') }}</AlertDialogTitle>
              <AlertDialogDescription>
                <template v-if="isDeposit">
                  {{ $t('Are you sure you want to cancel this deposit? This action cannot be undone.') }}
                </template>
                <template v-else>
                  {{ $t('Are you sure you want to cancel this payment? This action cannot be undone.') }}
                </template>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{{ isDeposit ? $t('Keep deposit') : $t('Keep payment') }}</AlertDialogCancel>
              <AlertDialogAction variant="destructive"  @click.prevent="onClickCancelPayment" >
                {{ isDeposit ? $t('Confirm deposit cancellation') : $t('Confirm payment cancellation') }}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>
